import { Banner } from '@/components'
import { Fade, JackInTheBox, Slide } from "react-awesome-reveal";
import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, store } from '@/store/store';
import { getStorageImagePath } from '@/utils/utils';
import ReCAPTCHA from "react-google-recaptcha";
import constants from '@/config/constants.json';
import { SeoTags } from '@/components/SeoTags';

export const Contact = () => {
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const appConfig = useSelector((state: RootState) => state.app.config);
    const page = appConfig?.contact?.page
    const settings = appConfig?.site_settings
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY?.trim() ?? "";

    const [formData, setFormData] = useState({
        name: "",
        subject: "",
        email: "",
        message: "",
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({
        email: false,
        message: false,
        subject: false,
        name: false,
    });
    const [response, setResponse] = useState("");
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const [recaptchaRenderKey, setRecaptchaRenderKey] = useState(0);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setResponse("");

        const email_regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const isEmailValid = email_regex.test(formData.email);
        setIsSubmitted(false);
        if (formData.email === '' || formData?.message === '' || formData?.name === '' || !isEmailValid) {
            setErrors({
                email: formData.email === '' || !isEmailValid,
                message: formData.message === '',
                subject: formData.subject === '',
                name: formData.name === '',
            });
            return;
        }

        if (!recaptchaSiteKey) {
            setError("reCAPTCHA is not configured yet. Please try again later.");
            return;
        }

        const currentRecaptchaToken = recaptchaToken || recaptchaRef.current?.getValue() || "";

        if (!currentRecaptchaToken) {
            setError("Please complete the reCAPTCHA verification.");
            return;
        }

        setError("");
        setErrors({
            email: false,
            message: false,
            subject: false,
            name: false,
        });
        setIsSubmitting(true);

        try {
            const request = await fetch(`${constants?.hosts?.backend}/api/cf-messages`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    full_name: formData.name,
                    email: formData.email,
                    subject: formData.subject || null,
                    message: formData.message,
                    recaptcha_token: currentRecaptchaToken,
                }),
            });

            const data = await request.json().catch(() => null);

            if (!request.ok) {
                const validationMessage = data?.errors
                    ? Object.values(data.errors).flat().find(Boolean)
                    : null;

                throw new Error(validationMessage || data?.message || "Failed to send the message. Please try again.");
            }

            setIsSubmitted(true);
            setFormData({ name: "", email: "", subject: "", message: "" });
            setResponse(data?.message || "Thanks for getting in touch. We received your message and will get back to you as soon as possible.");
            window.setTimeout(() => {
                setIsSubmitted(false);
            }, 5000);
            recaptchaRef.current?.reset();
            setRecaptchaToken("");
        } catch (err) {
            console.error("FAILED TO STORE CONTACT MESSAGE", err);
            setError(err instanceof Error ? err.message : "Failed to send the message. Please try again.");
        } finally {
            recaptchaRef.current?.reset();
            setRecaptchaToken("");
            setRecaptchaRenderKey((currentKey) => currentKey + 1);
            setIsSubmitting(false);
        }
    };
    if (!page) return

    return (
        <>
            <SeoTags
                page={page}
                pageType={"contact"}
                seo_settings={store.getState().app.config.seo_settings}
            />
            <JackInTheBox triggerOnce={true} delay={1000}>
                <Banner
                    subtitle={page?.banner_intro_text}
                    title={page?.banner_title}
                    image={getStorageImagePath(JSON.parse(page?.banner_image ?? {}))}
                    description={page?.banner_description}
                />
            </JackInTheBox>
            <div className=" contact-crev ">
                <div className="py-100 container mh-100v align-items-center d-flex ">
                    <div className="row ">
                        <div className="col-lg-6">
                            <Slide triggerOnce={true}>
                                <h6 className="circle-label text-white">
                                    <span>{page?.section_intro_text}</span>
                                </h6>
                            </Slide>
                            <Slide triggerOnce={true} delay={300}>
                                <h2 className="fs-50 text-white mt-3">{page?.section_title}</h2>
                            </Slide>
                        </div>
                        <div className="col-12"></div>
                        <div className="col-lg-6">
                            <Fade triggerOnce={true}>
                                <div className='ul-list text-white fs-16'
                                    dangerouslySetInnerHTML={{ __html: page?.section_text }}
                                />
                            </Fade>


                            {/* <h2 className="fs-50 text-white mt-3">Don't Hesitate  <br />To Reach Out </h2>
                            <p className='text-white fs-16 mt-3'>If you would like to work with us or just want to get in touch, we’d love to hear from you!</p> */}
                            <div className='py-4'>
                                <Fade triggerOnce={true}>
                                    <div className="d-flex align-items-center  mt-2">
                                        <div className="theme-btn btn-outline white circular-social-icon mr-md-3 mr-2" aria-hidden="true">
                                            <i className="fa-solid fa-location-dot"></i>
                                        </div>
                                        <div className=' text-white fs-16'>{settings.address_text}</div>
                                    </div>
                                </Fade>
                                <Fade triggerOnce={true} delay={200}>
                                    <div className="d-flex align-items-center mt-2 ">
                                        <div className="theme-btn btn-outline white circular-social-icon mr-md-3 mr-2" aria-hidden="true">
                                            <i className="fa-solid fa-phone fs-16"></i>
                                        </div>
                                        <a href={`tel:${settings?.phone_number}`} className=' text-white fs-16'>{settings?.phone_number}</a>
                                    </div>
                                </Fade>
                                <Fade triggerOnce={true} delay={400}>
                                    <div className="d-flex align-items-center mt-2 ">
                                        <div className="theme-btn btn-outline white circular-social-icon mr-md-3 mr-2" aria-hidden="true">
                                            <i className="fa-regular fa-envelope"></i>
                                        </div>
                                        <a href={`mailto:${settings?.email}`} className=' text-white fs-16 '>{settings?.email}</a>
                                    </div>
                                </Fade>
                            </div>

                            <h5 className='text-white  fw-700 mt-2'>{page?.follow_text}</h5>
                            <div className="d-flex align-items-center mt-2 flex-wrap ">
                                <Fade triggerOnce={true} delay={200}>
                                    <a href={settings?.facebook_url} className="theme-btn btn-outline white circular-social-icon  mt-2 mr-2" target='_blank' rel="noreferrer">
                                        <i className="fa-brands fa-facebook-f"></i>
                                    </a>
                                </Fade>
                                <Fade triggerOnce={true} delay={300}>
                                    <a href={settings?.instagram_url} className="theme-btn btn-outline white circular-social-icon  mt-2 mr-2" target='_blank' rel="noreferrer">
                                        <i className="fa-brands fa-instagram"></i>
                                    </a>
                                </Fade>
                                {/* <Fade triggerOnce={true} delay={400}>
                                    <a className="theme-btn btn-outline white circular-social-icon  mt-2 mr-2" target='_blank'>
                                        <i className="fa-brands fa-x-twitter"></i>
                                    </a>
                                </Fade> */}
                                {/* <Fade triggerOnce={true} delay={500}>
                                    <a className="theme-btn btn-outline white circular-social-icon  mt-2 mr-2" target='_blank'>
                                        <i className="fa-brands fa-tiktok"></i>
                                    </a>
                                </Fade> */}
                                <Fade triggerOnce={true} delay={600}>
                                    <a href={settings?.linkedin_url} className="theme-btn btn-outline white circular-social-icon  mt-2 mr-2" target='_blank' rel="noreferrer">
                                        <i className="fa-brands fa-linkedin-in"></i>
                                    </a>
                                </Fade>
                            </div>
                        </div>

                        <div className="col-lg-6 mt-lg-0 ">
                            <form action="" onSubmit={handleSubmit}>



                                <div className="row">
                                    <div className="col-lg-6  mt-3 mt-lg-0">
                                        <Fade triggerOnce={true}>
                                            <div className={`${errors?.name && formData?.name?.length === 0 ? "error" : ""}`}>
                                                <label htmlFor="contact-name" className='text-white fs-16 mb-2 d-block'>Name</label>
                                                <input id="contact-name" type="text" name='name' value={formData?.name} placeholder='Name' onChange={handleChange} autoComplete="name" />
                                            </div>
                                        </Fade>
                                    </div>
                                    <div className="col-lg-6  mt-3 mt-lg-0">
                                        <Fade triggerOnce={true} delay={200}>
                                            <div className={`${errors?.email && formData?.email?.length === 0 ? "error" : ""}`}>
                                                <label htmlFor="contact-email" className='text-white fs-16 mb-2 d-block'>Email</label>
                                                <input id="contact-email" type="email" name='email' placeholder='Email' value={formData?.email} onChange={handleChange} autoComplete="email" />
                                            </div>
                                        </Fade>
                                    </div>
                                    <div className="col-lg-12 mt-3">
                                        <Fade triggerOnce={true} delay={300}>
                                            <div className={`${errors?.subject && formData?.subject?.length === 0 ? "error" : ""}`}>
                                                <label htmlFor="contact-subject" className='text-white fs-16 mb-2 d-block'>Subject</label>
                                                <input id="contact-subject" type="text" name='subject' placeholder='Subject' value={formData?.subject} onChange={handleChange} autoComplete="off" />
                                            </div>
                                        </Fade>

                                    </div>

                                    <div className="col-lg-12 mt-3">
                                        <Fade triggerOnce={true} delay={400}>
                                            <div className={`${errors?.message && formData?.message?.length === 0 ? "error" : ""}`}>
                                                <label htmlFor="contact-message" className='text-white fs-16 mb-2 d-block'>Project Details</label>
                                                <textarea id="contact-message" name='message' placeholder='Tell us about your project' value={formData?.message} onChange={handleChange} rows={6} />
                                            </div>
                                        </Fade>
                                    </div>

                                    <div className="pl-3 mt-3 w-100" aria-live="polite">
                                        {error && <div className="alert alert-danger w-100 fs-16 mb-3" role="alert">
                                            {error}
                                        </div>}
                                        {isSubmitted && <div className="alert alert-success  w-100" role="alert">
                                            {response}
                                        </div>}
                                        <Fade triggerOnce={true} delay={600}>
                                            <ReCAPTCHA
                                                key={recaptchaRenderKey}
                                                ref={recaptchaRef}
                                                sitekey={recaptchaSiteKey}
                                                size="normal"
                                                onChange={(token) => {
                                                    setRecaptchaToken(token ?? "");
                                                    setError("");
                                                }}
                                                onExpired={() => {
                                                    setRecaptchaToken("");
                                                    setError("reCAPTCHA expired. Please verify again.");
                                                }}
                                                onErrored={() => {
                                                    setRecaptchaToken("");
                                                    setError("reCAPTCHA failed to load. Please refresh the page and try again.");
                                                }}
                                            />
                                            <button type="submit" style={{ background: 'transparent', border: 0 }} className='mt-3' disabled={isSubmitting}>
                                                <div className=" theme-btn btn-outline circular white w-auto d-flex" >
                                                    <span className='fs-16'>{isSubmitting ? "Sending..." : page?.form_btn_text}</span>
                                                </div>
                                            </button>

                                        </Fade>
                                    </div>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div >
        </>
    )
}
